// This panel handles the facets whose values are sent separately to the clause.
// ex: maritalStatus contains the values "inRelationship" and "hasChildren". But both are individual innerClauses.
// Because of their independent behavior, we call them "sandboxed chips".
import React, { useMemo } from "react";
import styled from "styled-components";
import { content } from "@glossy";
import { Facet } from "@linkfluence/models/facet";
import Panel from "@ui-kit/popover/panel";
import Chips from "@radarly/components/filterbar/panels/chips";
import { useFilterbarChoices } from "@radarly/core/filters/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import {
  getFieldFromInnerClause,
  getInnerClauseFromClause,
} from "@radarly/models/clause/getter";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { FullFacet } from "@radarly/models/facets";
import { Choice, Status } from "@radarly/models/filters";
import {
  deleteSandboxedClause,
  getNextClauseFromSandboxedValue,
} from "./clause";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

// The unified clause is usually computed by the filterbar.
// For example, the "inRelationship" and "hasChildren" innerClauses are combined into a single "maritalStatus" innerClause.
// But if the "maritalStatus" filter doesn't exist yet, we get a non-unified clause. So we have to do the work ourselves.
const unifyClause = (facet: Facet, clause: Clause): Clause => {
  const hasFacet = getInnerClauseFromClause(clause, facet);
  if (hasFacet) {
    return clause;
  } else {
    const innerClause = { [facet]: [] };
    const getSandboxedFields = (sandboxedFields: string[]) => {
      sandboxedFields.forEach((sandboxedField) => {
        const sandboxedInnerClause = getInnerClauseFromClause(
          clause,
          sandboxedField as Facet
        );
        if (sandboxedInnerClause) {
          const isIncluded = !!Object.values(sandboxedInnerClause)[0];
          if (isIncluded) {
            const field = getFieldFromInnerClause(sandboxedInnerClause);
            innerClause[facet].push(field);
          }
        }
      });
    };
    if (facet === Facet.MaritalStatus) {
      getSandboxedFields(["inRelationship", "hasChildren"]);
    }
    return { and: [innerClause] };
  }
};

const RadarlyFilterbarPanelsSandboxedChips = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) => {
  const unifiedClause = useMemo(
    () => unifyClause(facet.id as Facet, clause),
    [clause, facet.id]
  );
  const locale = useCurrentUserLocale();
  const onStatusChange = (choice: Choice, status: Status) => {
    setClause(
      getNextClauseFromSandboxedValue(unifiedClause, String(choice.id), status)
    );
  };
  const { choices } = useFilterbarChoices(unifiedClause, facet);
  const onClear = () => setClause(deleteSandboxedClause(unifiedClause));

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      size={choices.length < 5 ? "s" : "m"}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(unifiedClause) : false}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <Chips choices={choices} onSetChoices={onStatusChange} />
    </Panel>
  );
};

export default RadarlyFilterbarPanelsSandboxedChips;
